import type {
  IntegrationRow,
  IntegrationStatusInput,
  IntegrationStatusLabel,
  OverviewIntegrationBadge,
} from "./types";
import { LABEL } from "./terminology";

/**
 * Three-state integration status from API SSoT (Phase 4).
 * Do NOT collapse Configured into Not configured.
 */
export function mapIntegrationStatus(row: {
  connected?: boolean;
  configured?: boolean;
} | null | undefined): IntegrationStatusLabel {
  if (!row) return LABEL.notConfigured as IntegrationStatusLabel;
  if (row.connected) return "Connected";
  if (row.configured) return "Configured";
  return LABEL.notConfigured as IntegrationStatusLabel;
}

export function integrationStatusLabel(status: IntegrationStatusLabel): string {
  return status;
}

const FALLBACK_INTEGRATIONS: { id: string; name: string }[] = [
  { id: "openai", name: "OpenAI" },
  { id: "shopify", name: "Shopify" },
  { id: "email", name: "Email" },
  { id: "stripe", name: "Stripe" },
  { id: "webhooks", name: "Webhooks" },
];

/**
 * Derive integration rows.
 * `null` / undefined = not loaded → Not available verification; status Not configured
 *   only as placeholder rows (UI should prefer showing Unknown when null if desired).
 * Empty array = loaded with no integrations.
 */
export function deriveIntegrations(
  integrations?: IntegrationStatusInput[] | null,
): IntegrationRow[] {
  if (integrations == null) {
    return FALLBACK_INTEGRATIONS.map((r) => ({
      ...r,
      status: LABEL.notConfigured as IntegrationStatusLabel,
      lastVerification: LABEL.notAvailable,
      configured: false,
      connected: false,
    }));
  }

  return integrations.map((row) => ({
    id: row.id,
    name: row.name,
    status: mapIntegrationStatus(row),
    lastVerification: (row.last_verification ?? "").trim() || LABEL.notAvailable,
    configured: Boolean(row.configured),
    connected: Boolean(row.connected),
  }));
}

export function deriveOverviewIntegrations(
  integrations?: IntegrationStatusInput[] | null,
): OverviewIntegrationBadge[] {
  const rows = deriveIntegrations(integrations);
  const wanted: { id: string; label: string }[] = [
    { id: "openai", label: "AI" },
    { id: "email", label: "Email" },
    { id: "shopify", label: "Shopify" },
    { id: "stripe", label: "Stripe" },
  ];
  return wanted.map(({ id, label }) => {
    const row = rows.find((r) => r.id === id);
    return {
      id,
      label,
      status: row?.status ?? (LABEL.notConfigured as IntegrationStatusLabel),
    };
  });
}

export type IntegrationToneKey = "success" | "warning" | "neutral";

export function integrationToneKey(status: IntegrationStatusLabel): IntegrationToneKey {
  if (status === "Connected") return "success";
  if (status === "Configured") return "warning";
  return "neutral";
}
